import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { createChart } from "lightweight-charts";
import { API } from "../components/atlas/helpers";
import { useAtlas } from "../store/atlasStore";
import { RefreshCw, Plus, X, TrendingUp, TrendingDown, Minus } from "lucide-react";

function fmt(v, d = 2) {
  if (v == null || !isFinite(+v)) return "—";
  return Number(v).toFixed(d);
}

function pct(v, showSign = true) {
  if (v == null || !isFinite(+v)) return "—";
  const n = Number(v);
  return `${showSign && n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

function toUnix(isoStr) {
  return Math.floor(new Date(isoStr).getTime() / 1000);
}

function MiniEquityCurve({ curve }) {
  if (!curve?.length || curve.length <= 1) {
    return (
      <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-tertiary)", fontSize: 10, background: "var(--bg-raised)", border: "1px solid var(--border-dim)", borderRadius: 4 }}>
        No historical trades (flat equity)
      </div>
    );
  }

  const vals = curve.map(c => c.equity ?? c.value ?? 0);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const w = 400, h = 80;
  const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * w},${h - ((v - min) / range) * (h - 8) - 4}`).join(" ");
  const isPos = vals[vals.length - 1] >= vals[0];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: 80 }} preserveAspectRatio="none">
      <defs>
        <linearGradient id="eq-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={isPos ? "var(--green)" : "var(--red)"} stopOpacity="0.3" />
          <stop offset="100%" stopColor={isPos ? "var(--green)" : "var(--red)"} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,${h} ${pts} ${w},${h}`}
        fill="url(#eq-grad)"
      />
      <polyline
        points={pts}
        fill="none"
        stroke={isPos ? "var(--green)" : "var(--red)"}
        strokeWidth="1.5"
      />
    </svg>
  );
}

export function PaperWorkspace() {
  const { state } = useAtlas();
  const { provider, symbol, interval } = state;

  const [account, setAccount] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [positions, setPositions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);

  // OHLCV & Recommendation data
  const [bars, setBars] = useState([]);
  const [recommendation, setRecommendation] = useState(null);

  // Position form state
  const [newSide, setNewSide] = useState("long");
  const [newSize, setNewSize] = useState(10);
  const [newSl, setNewSl] = useState("");
  const [newTp, setNewTp] = useState("");
  const [openingPos, setOpeningPos] = useState(false);

  // Chart refs
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const candlestickSeriesRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setRecommendation(null); // Clear old recommendation while new one computes
    
    try {
      // 1. Fetch fast data together so the UI paints instantly
      const [accR, analR, posR, tradeR, ohlcvR] = await Promise.allSettled([
        axios.get(`${API}/paper/account`),
        axios.get(`${API}/paper/analytics`),
        axios.get(`${API}/paper/positions/open`),
        axios.get(`${API}/paper/trades`, { params: { limit: 100 } }),
        axios.get(`${API}/market/ohlcv`, { params: { provider, symbol, interval, period: "1y", limit: 300 } }),
      ]);

      if (accR.status === "fulfilled") setAccount(accR.value.data);
      if (analR.status === "fulfilled") setAnalytics(analR.value.data);
      if (posR.status === "fulfilled") setPositions(posR.value.data);
      if (tradeR.status === "fulfilled") setTrades(tradeR.value.data);
      if (ohlcvR.status === "fulfilled") setBars(ohlcvR.value.data.bars || []);
    } catch {}
    
    // UI is ready to display market data and portfolio
    setLoading(false);

    // 2. Fetch the slow recommendation asynchronously in the background
    try {
      const recBody = {
        provider, symbol, interval, period: "2y", horizon: 5,
        include_models: false,
      };
      const recR = await axios.post(`${API}/decision/recommend`, recBody);
      setRecommendation(recR.data);
    } catch {}
  }, [provider, symbol, interval]);

  useEffect(() => { refresh(); }, [refresh]);

  const [lastRecRef, setLastRecRef] = useState(null);

  // Set default side and SL/TP based on Atlas recommendation, allowing override
  useEffect(() => {
    if (recommendation && bars.length > 0) {
      const recDir = recommendation.decision; // "UP", "DOWN", "WAIT", "LONG", "SHORT"
      let side = newSide;

      // Only auto-change the side dropdown if we got a NEW recommendation object
      if (recommendation !== lastRecRef) {
        if (recDir === "UP" || recDir === "LONG") side = "long";
        else if (recDir === "DOWN" || recDir === "SHORT") side = "short";
        setNewSide(side);
        setLastRecRef(recommendation);
      }

      const sizing = recommendation.sizing || {};
      const refPrice = bars[bars.length - 1]?.close;

      if (refPrice && sizing.stop_pct != null && sizing.take_profit_pct != null) {
        const stopPct = Number(sizing.stop_pct) / 100;
        const tpPct = Number(sizing.take_profit_pct) / 100;
        
        if (!isNaN(stopPct) && !isNaN(tpPct)) {
          if (side === "long") {
            setNewSl((refPrice * (1 - stopPct)).toFixed(4));
            setNewTp((refPrice * (1 + tpPct)).toFixed(4));
          } else {
            setNewSl((refPrice * (1 + stopPct)).toFixed(4));
            setNewTp((refPrice * (1 - tpPct)).toFixed(4));
          }
        }
      }
    }
  }, [recommendation, bars, newSide, lastRecRef]);

  // Initialize and update Lightweight Candlestick Chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Clean up previous chart instance
    if (chartInstanceRef.current) {
      chartInstanceRef.current.remove();
      chartInstanceRef.current = null;
    }

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { color: "#0f1117" },
        textColor: "#9ca3af",
      },
      grid: {
        vertLines: { color: "rgba(31, 41, 55, 0.5)" },
        horzLines: { color: "rgba(31, 41, 55, 0.5)" },
      },
      crosshair: {
        mode: 0,
      },
      timeScale: {
        borderColor: "rgba(31, 41, 55, 0.8)",
        timeVisible: true,
      },
      rightPriceScale: {
        borderColor: "rgba(31, 41, 55, 0.8)",
      },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight || 250,
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#10b981",
      downColor: "#ef4444",
      borderVisible: false,
      wickUpColor: "#10b981",
      wickDownColor: "#ef4444",
    });

    chartInstanceRef.current = chart;
    candlestickSeriesRef.current = candleSeries;

    // Resize handler using ResizeObserver to fill flex container
    const ro = new ResizeObserver(() => {
      if (chartInstanceRef.current && chartContainerRef.current) {
        chartInstanceRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      }
    });
    ro.observe(chartContainerRef.current);

    return () => {
      ro.disconnect();
      if (chartInstanceRef.current) {
        chartInstanceRef.current.remove();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  // Update chart bars
  useEffect(() => {
    if (candlestickSeriesRef.current && bars.length > 0) {
      const formatted = bars.map(b => ({
        time: toUnix(b.time),
        open: b.open,
        high: b.high,
        low: b.low,
        close: b.close,
      })).sort((a, b) => a.time - b.time);
      candlestickSeriesRef.current.setData(formatted);
      chartInstanceRef.current?.timeScale().fitContent();
    }
  }, [bars]);

  const markAll = async () => {
    setMarking(true);
    try { await axios.post(`${API}/paper/positions/mark`, { auto_close_on_tp_sl: true }); await refresh(); } catch {}
    setMarking(false);
  };

  const openPosition = async () => {
    setOpeningPos(true);
    try {
      await axios.post(`${API}/paper/positions/open`, {
        provider, symbol, interval,
        side: newSide,
        size_pct: +newSize,
        stop_price: newSl ? +newSl : null,
        take_profit_price: newTp ? +newTp : null,
      });
      await refresh();
      setNewSl(""); setNewTp("");
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setOpeningPos(false);
  };

  const closePosition = async (posId) => {
    try { await axios.post(`${API}/paper/positions/${posId}/close`, {}); await refresh(); } catch (e) { alert(e.message); }
  };

  const resetAccount = async () => {
    if (!window.confirm("Reset account? All history will be lost.")) return;
    await axios.post(`${API}/paper/account/reset`, {});
    await refresh();
  };

  const metrics = analytics?.metrics || {};
  const curve = analytics?.equity_curve || [];
  const acc = account?.account || {};
  const equity = analytics?.equity ?? account?.equity ?? 0;

  // Custom recommendation glow style
  const recDir = recommendation?.decision || "WAIT";
  const recConf = recommendation?.confidence ?? 0;
  const recPillStyle = recDir === "UP"
    ? { background: "linear-gradient(135deg, rgba(22, 163, 74, 0.2) 0%, rgba(22, 163, 74, 0.05) 100%)", border: "1px solid rgba(22, 163, 74, 0.45)", color: "rgb(74, 222, 128)" }
    : recDir === "DOWN"
    ? { background: "linear-gradient(135deg, rgba(220, 38, 38, 0.2) 0%, rgba(220, 38, 38, 0.05) 100%)", border: "1px solid rgba(220, 38, 38, 0.45)", color: "rgb(248, 113, 113)" }
    : { background: "linear-gradient(135deg, rgba(100, 116, 139, 0.2) 0%, rgba(100, 116, 139, 0.05) 100%)", border: "1px solid rgba(100, 116, 139, 0.45)", color: "rgb(203, 213, 225)" };

  const recStopPct = recommendation?.sizing?.stop_pct;
  const recTpPct = recommendation?.sizing?.take_profit_pct;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "280px 1fr 340px", height: "100%", gap: 4, padding: 4 }}>

      {/* ── LEFT COLUMN: Portfolio & Positions List ────────────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, height: "100%", minHeight: 0 }}>
        {/* Portfolio Stats */}
        <div className="panel" style={{ flexShrink: 0 }}>
          <div className="panel-header">
            <span className="panel-title">Portfolio</span>
            <div style={{ display: "flex", gap: 4 }}>
              <button className="btn btn-ghost btn-icon" onClick={refresh} disabled={loading}>
                <RefreshCw size={11} className={loading ? "spin" : ""} />
              </button>
              <button className="btn btn-ghost" style={{ fontSize: 9, padding: "2px 5px" }} onClick={resetAccount}>Reset</button>
            </div>
          </div>
          <div className="panel-body" style={{ padding: "8px 10px" }}>
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "uppercase" }}>Equity</div>
              <div style={{ fontSize: 18, fontFamily: "'JetBrains Mono'", fontWeight: 700 }}>
                ${Number(equity).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            {[
              ["Cash",            `$${Number(acc.cash ?? acc.balance ?? 0).toLocaleString("en", { maximumFractionDigits: 0 })}`],
              ["Starting",        `$${Number(acc.starting_balance ?? 100000).toLocaleString("en", { maximumFractionDigits: 0 })}`],
              ["Closed Trades",   analytics?.closed_trade_count ?? trades.length],
              ["Open Positions",  positions.length],
            ].map(([k, v]) => (
              <div className="kv-row" key={k} style={{ padding: "2px 0" }}>
                <span className="k" style={{ fontSize: 9 }}>{k}</span>
                <span className="v mono" style={{ fontSize: 9 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Open Positions List */}
        <div className="panel" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="panel-header">
            <span className="panel-title">Open Positions</span>
            <button className="btn btn-ghost" style={{ fontSize: 9, padding: "2px 6px" }} onClick={markAll} disabled={marking}>
              {marking ? "Marking…" : "Mark All"}
            </button>
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {positions.map(p => {
              const upnl = p.unrealized_pnl ?? 0;
              return (
                <div key={p.position_id} style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-dim)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 11 }}>{p.symbol}</span>
                      <span className={`badge ${p.side === "long" ? "badge-green" : "badge-red"}`} style={{ marginLeft: 4 }}>
                        {p.side}
                      </span>
                    </div>
                    <button className="btn btn-ghost btn-icon" onClick={() => closePosition(p.position_id)}>
                      <X size={11} />
                    </button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, marginTop: 4, fontSize: 9 }}>
                    <div><span className="text-dim">Entry: </span><span className="mono">{fmt(p.entry_price, 4)}</span></div>
                    <div><span className="text-dim">Mark: </span><span className="mono">{fmt(p.last_mark_price, 4)}</span></div>
                    <div>
                      <span className="text-dim">PnL: </span>
                      <span className="mono" style={{ color: upnl >= 0 ? "var(--green)" : "var(--red)" }}>
                        {upnl >= 0 ? "+" : ""}{fmt(upnl, 2)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            {!positions.length && (
              <div style={{ padding: 16, color: "var(--text-tertiary)", fontSize: 11, textAlign: "center" }}>No open positions</div>
            )}
          </div>
        </div>
      </div>

      {/* ── MIDDLE COLUMN: OHLCV Chart + Recommendation Console ─────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, height: "100%" }}>
        {/* Candlestick Chart */}
        <div className="panel" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="panel-header">
            <span className="panel-title">{symbol} Price Feed ({interval})</span>
            {bars.length > 0 && <span className="badge badge-outline" style={{ textTransform: "none", fontSize: 9 }}>{bars.length} bars loaded</span>}
          </div>
          <div ref={chartContainerRef} style={{ flex: 1, position: "relative", minHeight: 180, background: "#0f1117" }} />
        </div>

        {/* Execution & Recommendation Console */}
        <div className="panel" style={{ flexShrink: 0 }}>
          <div className="panel-header"><span className="panel-title">Order Execution Console</span></div>
          <div className="panel-body" style={{ padding: "8px 10px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 8, alignItems: "center" }}>
              {/* Left side: Atlas Recommendation */}
              <div style={{ borderRight: "1px solid var(--border-dim)", paddingRight: 8 }}>
                <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "uppercase", marginBottom: 3 }}>Atlas Recommendation</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    ...recPillStyle,
                    fontSize: 12, padding: "3px 10px", borderRadius: 3, display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700
                  }}>
                    {recDir === "UP" ? <TrendingUp size={11} /> : recDir === "DOWN" ? <TrendingDown size={11} /> : <Minus size={11} />}
                    {recDir}
                  </span>
                  <div>
                    <div style={{ fontSize: 13, fontFamily: "'JetBrains Mono'", fontWeight: 700, lineHeight: 1 }}>
                      {(recConf * 100).toFixed(1)}%
                    </div>
                    <span style={{ fontSize: 8, color: "var(--text-tertiary)" }}>confidence</span>
                  </div>
                </div>
                {recStopPct != null && recTpPct != null && (
                  <div style={{ display: "flex", gap: 12, marginTop: 6, padding: "4px 8px", background: "rgba(255,255,255,0.03)", borderRadius: 4 }}>
                    <div>
                      <div style={{ fontSize: 8, color: "var(--text-tertiary)" }}>Rec. Stop</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--red)" }}>{recStopPct}%</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 8, color: "var(--text-tertiary)" }}>Rec. TP</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--green)" }}>{recTpPct}%</div>
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 8, color: "var(--text-tertiary)", marginTop: 6 }}>
                  Pre-fills order form (overwritable below).
                </div>
              </div>

              {/* Right side: Position Form fields */}
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: "4px 6px", marginBottom: 6 }}>
                  <div>
                    <div className="label" style={{ fontSize: 8, marginBottom: 1 }}>Order Side</div>
                    <select className="inp" value={newSide} onChange={e => setNewSide(e.target.value)} style={{ height: 20, padding: "0 4px", fontSize: 9 }}>
                      <option value="long">Buy / Long</option>
                      <option value="short">Sell / Short</option>
                    </select>
                  </div>
                  <div>
                    <div className="label" style={{ fontSize: 8, marginBottom: 1 }}>Alloc Size %</div>
                    <input type="number" className="inp" value={newSize} min={0.1} max={100}
                      onChange={e => setNewSize(+e.target.value)} style={{ height: 20, fontSize: 9 }} />
                  </div>
                  <div>
                    <div className="label" style={{ fontSize: 8, marginBottom: 1 }}>Stop Loss Price</div>
                    <input type="number" className="inp" value={newSl} placeholder="optional"
                      onChange={e => setNewSl(e.target.value)} style={{ height: 20, fontSize: 9 }} />
                  </div>
                  <div>
                    <div className="label" style={{ fontSize: 8, marginBottom: 1 }}>Take Profit Price</div>
                    <input type="number" className="inp" value={newTp} placeholder="optional"
                      onChange={e => setNewTp(e.target.value)} style={{ height: 20, fontSize: 9 }} />
                  </div>
                </div>
                <button
                  className={`btn ${newSide === "long" ? "btn-pos" : "btn-danger"}`}
                  style={{ width: "100%", justifyContent: "center", fontSize: 10, height: 22, padding: 0 }}
                  onClick={openPosition}
                  disabled={openingPos}
                >
                  {openingPos ? <RefreshCw size={10} className="spin" /> : <Plus size={10} />}
                  {openingPos ? "Transacting…" : `Execute Paper ${newSide === "long" ? "LONG" : "SHORT"}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── RIGHT COLUMN: Equity Curve & Trade History ──────────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, height: "100%", minHeight: 0 }}>
        {/* Equity Curve */}
        <div className="panel" style={{ flexShrink: 0 }}>
          <div className="panel-header">
            <span className="panel-title">Equity Curve</span>
            <span className="badge" style={{ color: (metrics.realized_pnl_pct ?? 0) >= 0 ? "var(--green)" : "var(--red)" }}>
              {pct(metrics.realized_pnl_pct)}
            </span>
          </div>
          <div className="panel-body" style={{ padding: "6px" }}>
            <MiniEquityCurve curve={curve} />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 4, marginTop: 6 }}>
              {[
                { label: "Return",  val: pct(metrics.realized_pnl_pct), col: (metrics.realized_pnl_pct ?? 0) >= 0 ? "var(--green)" : "var(--red)" },
                { label: "Sharpe",  val: fmt(metrics.sharpe) },
                { label: "Max DD",  val: `${fmt(metrics.max_drawdown_pct)}%`, col: "var(--red)" },
                { label: "Win Rate",val: metrics.win_rate != null ? `${(metrics.win_rate*100).toFixed(0)}%` : "—" },
                { label: "Trades",  val: analytics?.closed_trade_count ?? "—" },
                { label: "P. Factor", val: fmt(metrics.profit_factor) },
              ].map(m => (
                <div key={m.label} className="stat-card" style={{ padding: "4px 6px" }}>
                  <div className="s-label" style={{ fontSize: 7 }}>{m.label}</div>
                  <div className="s-val" style={{ fontSize: 10, color: m.col || "var(--text-primary)", fontWeight: 600 }}>{m.val}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trade History */}
        <div className="panel" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="panel-header">
            <span className="panel-title">Trade History</span>
            <a
              href={`${API}/paper/trades.csv`}
              download="atlas_trades.csv"
              className="btn btn-ghost"
              style={{ fontSize: 8, padding: "1px 4px", textDecoration: "none" }}
            >
              CSV
            </a>
          </div>
          <div className="table-container" style={{ flex: 1, overflowY: "auto" }}>
            <table className="dt">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Side</th>
                  <th className="r">Entry</th>
                  <th className="r">Exit</th>
                  <th className="r">PnL</th>
                </tr>
              </thead>
              <tbody>
                {trades.map(t => {
                  const pnl = t.realized_pnl ?? 0;
                  return (
                    <tr key={t.trade_id}>
                      <td style={{ fontSize: 8 }}>{t.opened_at?.slice(5,10)}</td>
                      <td><span className={`badge ${t.side === "long" ? "badge-green" : "badge-red"}`}>{t.side}</span></td>
                      <td className="r mono" style={{ fontSize: 9 }}>{fmt(t.entry_price, 2)}</td>
                      <td className="r mono" style={{ fontSize: 9 }}>{fmt(t.exit_price, 2)}</td>
                      <td className="r mono" style={{ color: pnl >= 0 ? "var(--green)" : "var(--red)", fontSize: 9, fontWeight: 600 }}>
                        {pnl >= 0 ? "+" : ""}{fmt(pnl, 0)}
                      </td>
                    </tr>
                  );
                })}
                {!trades.length && (
                  <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: 16 }}>No trades</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
}
